import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { ArrowLeft, ChevronLeft, ChevronRight, Apple, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBuy = async () => {
    if (!product) return;
    setBuying(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        product_id: product.id,
        origin_url: origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="product-loading" className="pt-40 text-center text-stone-500">
        Loading…
      </div>
    );
  }
  if (!product) {
    return (
      <div data-testid="product-not-found" className="pt-40 text-center text-stone-300">
        <p className="text-overline text-amber-600 mb-4">Not found</p>
        <p>That piece may have already sold.</p>
        <Link to="/shop" className="inline-block mt-6 text-amber-500 underline">
          ← Back to shop
        </Link>
      </div>
    );
  }

  const imgs = product.images || [];

  const next = () => setActiveImg((i) => (i + 1) % imgs.length);
  const prev = () => setActiveImg((i) => (i - 1 + imgs.length) % imgs.length);

  return (
    <div data-testid="product-page" className="bg-stone-950 text-stone-50 pt-12 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
        <button
          onClick={() => navigate(-1)}
          data-testid="product-back"
          className="text-overline text-stone-400 hover:text-amber-500 flex items-center gap-2 mb-10"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/5] bg-stone-900 overflow-hidden border border-stone-800">
              {imgs[activeImg] && (
                <img
                  src={imgUrl(imgs[activeImg])}
                  alt={product.name}
                  className="w-full h-full object-contain p-6"
                />
              )}
              {imgs.length > 1 && (
                <>
                  <button
                    aria-label="Previous"
                    onClick={prev}
                    data-testid="gallery-prev"
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-stone-950/70 hover:bg-amber-700 backdrop-blur-sm text-stone-50 p-3 transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    aria-label="Next"
                    onClick={next}
                    data-testid="gallery-next"
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-stone-950/70 hover:bg-amber-700 backdrop-blur-sm text-stone-50 p-3 transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3" data-testid="thumb-row">
                {imgs.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-square overflow-hidden border ${
                      i === activeImg ? "border-amber-600" : "border-stone-800 hover:border-stone-600"
                    }`}
                  >
                    <img src={imgUrl(src)} alt="" className="w-full h-full object-contain p-1 bg-stone-950" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <p className="text-overline text-amber-600 mb-4">{product.category}</p>
            <h1
              data-testid="product-title"
              className="font-display text-4xl sm:text-5xl font-bold tracking-tight"
            >
              {product.name}
            </h1>
            <p
              data-testid="product-price"
              className="font-display text-3xl text-stone-50 mt-6"
            >
              ${product.price?.toFixed(2)}{" "}
              <span className="text-stone-500 text-base uppercase tracking-widest">
                {product.currency}
              </span>
            </p>

            <div className="h-px bg-stone-800 my-8" />

            <p className="text-stone-300 leading-relaxed text-base">
              {product.description}
            </p>

            <div className="mt-8">
              <p className="text-overline text-stone-500 mb-3">Materials</p>
              <p className="text-stone-300">{product.materials}</p>
            </div>

            <div className="mt-10 flex flex-col gap-3">
              {product.external_url && (
                <a
                  href={product.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="external-url-btn"
                  className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-8 py-5 text-overline transition-colors flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <ShoppingBag size={18} />
                    {product.external_url_label || "Try it / Pick up at partner shop"}
                  </span>
                  <span aria-hidden>↗</span>
                </a>
              )}
              <button
                onClick={handleBuy}
                disabled={!product.in_stock || buying}
                data-testid="buy-now-btn"
                className={`${
                  product.external_url
                    ? "bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:border-amber-600"
                    : "bg-amber-700 hover:bg-amber-600"
                } disabled:bg-stone-800 disabled:cursor-not-allowed text-stone-50 px-8 py-5 text-overline transition-colors flex items-center justify-between`}
              >
                <span className="flex items-center gap-3">
                  <ShoppingBag size={18} />
                  {product.in_stock
                    ? buying
                      ? "Starting checkout…"
                      : product.external_url
                      ? "Or purchase online"
                      : "Buy now"
                    : "Sold"}
                </span>
                <span className="text-stone-50">${product.price?.toFixed(0)}</span>
              </button>
              <button
                onClick={handleBuy}
                disabled={!product.in_stock || buying}
                data-testid="apple-pay-btn"
                className="bg-stone-50 hover:bg-stone-200 disabled:bg-stone-700 disabled:text-stone-500 disabled:cursor-not-allowed text-stone-950 px-8 py-5 text-overline flex items-center justify-center gap-3 transition-colors"
              >
                <Apple size={18} fill="currentColor" /> Pay
              </button>
              <p className="text-xs text-stone-500 mt-2 leading-relaxed">
                {product.external_url
                  ? "Try the piece in person at our partner shop, or buy online for direct shipping. Apple Pay shows automatically on supported devices."
                  : "Secure checkout via Stripe. Apple Pay shows automatically on supported devices. One-of-one piece — when it sells, it's gone."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
